import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Segmented, Select, Space, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api.service';
import type { ReportParams } from '@/services/reportDocuments.service';
import ReportDocumentPanel from '@/components/finance/report-document/ReportDocumentPanel';
import PeriodRangeFilter from '@/components/finance/report-document/PeriodRangeFilter';
import { isoDate } from '@/components/finance/report-document/periodPresets';

const { Title, Text } = Typography;

type Mode = 'activity' | 'open_items';

/** Supplier statement of account built from posted AP documents (server document: screen, PDF, Excel, email). */
export const SupplierStatementPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTo = searchParams.get('to');
  const [supplierId, setSupplierId] = useState<string | undefined>(searchParams.get('supplier_id') ?? undefined);
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    searchParams.get('from') ? dayjs(searchParams.get('from')) : (urlTo ? dayjs(urlTo) : dayjs()).startOf('year'),
    urlTo ? dayjs(urlTo) : dayjs(),
  ]);
  const [mode, setMode] = useState<Mode>(searchParams.get('mode') === 'open_items' ? 'open_items' : 'activity');
  const [currency, setCurrency] = useState<string | undefined>(searchParams.get('currency') ?? undefined);
  const [params, setParams] = useState<ReportParams | null>(null);

  const { data: suppliers = [] } = useQuery({
    queryKey: ['statement-suppliers'],
    queryFn: async () => {
      const res = await api.get<any>('/procurement/suppliers');
      const data = (res as any)?.data ?? res;
      return (Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []) as any[];
    },
  });
  const supplierOptions = useMemo(
    () => suppliers.map((s) => ({ value: s.id as string, label: `${s.supplier_name ?? ''}${s.supplier_code ? ` (${s.supplier_code})` : ''}` })),
    [suppliers]
  );

  const generate = () => {
    if (!supplierId) return;
    const next: ReportParams = { supplier_id: supplierId, from: isoDate(range[0]), to: isoDate(range[1]), mode };
    if (currency) next.currency = currency;
    setParams(next);
    setSearchParams(next as Record<string, string>, { replace: true });
  };

  useEffect(() => {
    if (searchParams.get('supplier_id') && !params) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="supplier-statement-page">
      <Title level={4}>{t('supplierStatement')}</Title>
      <ReportDocumentPanel
        reportKey="supplier-statement"
        params={params}
        emailLabel={t('emailToSupplier')}
        emptyHint={t('selectSupplierPeriodGenerate')}
        filters={
          <Space wrap size="middle" align="end">
            <Space direction="vertical" size={4}>
              <Text type="secondary">{t('supplier')}</Text>
              <Select
                placeholder={t('selectSupplier')}
                value={supplierId}
                onChange={setSupplierId}
                options={supplierOptions}
                style={{ width: 320 }}
                showSearch
                optionFilterProp="label"
              />
            </Space>
            <PeriodRangeFilter value={range} onChange={setRange} initialPreset="ytd" />
            <Space direction="vertical" size={4}>
              <Text type="secondary">{t('currency')}</Text>
              <Select
                allowClear
                placeholder={t('all')}
                value={currency}
                onChange={setCurrency}
                style={{ width: 100 }}
                options={['AED', 'USD', 'EUR', 'GBP', 'SAR', 'INR'].map((c) => ({ value: c, label: c }))}
              />
            </Space>
            <Segmented<Mode>
              value={mode}
              onChange={setMode}
              options={[
                { value: 'activity', label: t('activity') },
                { value: 'open_items', label: t('openItems') },
              ]}
            />
            <Button type="primary" icon={<SearchOutlined />} disabled={!supplierId} onClick={generate}>
              {tc('generate')}
            </Button>
          </Space>
        }
      />
    </div>
  );
};

export default SupplierStatementPage;
